import i18n from './i18n';

const EARTH_RADIUS_M = 6371000;

/**
 * Calculates the Haversine distance between two points on the Earth's surface.
 * This formula accounts for the spherical shape of the Earth and calculates
 * the shortest distance between two points given their latitude and longitude.
 *
 * For more information on the Haversine formula, see:
 * https://en.wikipedia.org/wiki/Haversine_formula
 *
 * @param {number} lat1 - Latitude of the first point in decimal degrees.
 * @param {number} lon1 - Longitude of the first point in decimal degrees.
 * @param {number} lat2 - Latitude of the second point in decimal degrees.
 * @param {number} lon2 - Longitude of the second point in decimal degrees.
 * @returns {number} The shortest distance between the two points on the surface of the Earth, in meters.
 */
export const getMetersBetween = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number => {
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_M * c;
};

/**
 * Calculates the Haversine distance between two points on the Earth's surface,
 * and converts the result into a human-readable string based on the given country code.
 *
 * For more information on the Haversine formula, see:
 * https://en.wikipedia.org/wiki/Haversine_formula
 *
 * @param {number} lat1 - Latitude of the first point in decimal degrees.
 * @param {number} lon1 - Longitude of the first point in decimal degrees.
 * @param {number} lat2 - Latitude of the second point in decimal degrees.
 * @param {number} lon2 - Longitude of the second point in decimal degrees.
 * @param {string} countryCode - The country code to format the distance (e.g., 'US' for feet/miles, others for meters/kilometers).
 * @returns {string} The distance in a human-readable format (e.g., miles, feet, meters, kilometers).
 */
export const getFormattedDistanceBetween = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
  countryCode: string,
): string => {
  const distanceInMeters = getMetersBetween(lat1, lon1, lat2, lon2);
  return distanceByCountryCode(distanceInMeters, countryCode);
};

/**
 * Converts a distance in meters to a localized string representation based on the country code.
 *
 * @param {number} meters - The distance in meters.
 * @param {string | undefined} country_code - The country code to format the distance (e.g., 'US' for feet/miles, others for meters/kilometers).
 * @returns {string} The distance in a human-readable format (e.g., miles, feet, meters, kilometers).
 */
export const distanceByCountryCode = (
  meters: number,
  country_code: string | undefined,
): string => {
  if (country_code === 'US') {
    const feet = metersToFeet(meters);
    if (feet >= 1000) {
      return i18n.t('miles', { value: metersToMiles(meters) });
    } else {
      return i18n.t('feet', { value: feet });
    }
  } else {
    if (meters <= 500) {
      return i18n.t('meters', { value: Math.round(meters) });
    } else {
      return i18n.t('kilometers', { value: metersToKilometers(meters) });
    }
  }
};

/**
 * Converts degrees to radians.
 *
 * @param {number} degrees - The angle in degrees.
 * @returns {number} The angle in radians.
 */
const toRadians = (degrees: number): number => {
  return degrees * (Math.PI / 180);
};

/**
 * Converts a distance in meters to miles, rounded to 1 decimal place.
 *
 * @param {number} meters - The distance in meters.
 * @returns {number} The distance in miles, rounded to 1 decimal place.
 */
const metersToMiles = (meters: number): number => {
  const miles = meters * 0.000621371;
  return Math.round(miles * 10) / 10;
};

/**
 * Converts a distance in meters to kilometers, rounded to 1 decimal place.
 *
 * @param {number} meters - The distance in meters.
 * @returns {number} The distance in kilometers, rounded to 1 decimal place.
 */
const metersToKilometers = (meters: number): number => {
  const km = meters / 1000;
  return Math.round(km * 10) / 10;
};

/**
 * Converts a distance in meters to feet, rounded to the nearest integer.
 *
 * @param {number} meters - The distance in meters.
 * @returns {number} The distance in feet, rounded to the nearest integer.
 */
const metersToFeet = (meters: number): number => {
  const ft = meters * 3.281;
  return Math.round(ft);
};
